import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, StyleSheet, Image, Alert, Platform } from 'react-native';
import { showActionSheet } from '../../components/ui/showActionSheet';
import { SvgIcon } from '../../components/common/SvgIcon';
import { ButtonLoader } from '../../components/load-main/ButtonLoader';
import { Clickable, PRESS_PRESETS } from '../../components/common/Clickable';
import { Input } from '../../components/common/Input';
import { BottomSheetPopup } from '../../components/common/BottomSheetPopup';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { DASH_STUDIO_RADIUS } from '../../shared/theme/studio/dashboardConstants';
import { useTranslation, formatNumber } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import type { CreatePostPayload } from '../../data/domains/community';
import { COMMUNITY_POST_MAX_CHARS } from './PostCard';

let launchCamera: ((options: object, callback: (res: any) => void) => void) | undefined;
let launchImageLibrary: ((options: object, callback: (res: any) => void) => void) | undefined;
try {
  const imagePicker = require('react-native-image-picker');
  launchCamera = imagePicker.launchCamera;
  launchImageLibrary = imagePicker.launchImageLibrary;
} catch {
  /* optional native module */
}

function createPostModalStyles(c: ThemeColors) {
  return StyleSheet.create({
    content: {
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.md,
      gap: appTheme.spacing.md,
    },
    composerMeta: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginTop: -appTheme.spacing.sm,
    },
    charsText: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      fontWeight: '500',
    },
    uploadCard: {
      backgroundColor: c.surfaceLight ?? c.background,
      borderRadius: DASH_STUDIO_RADIUS,
      padding: appTheme.spacing.md,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderSubtle,
    },
    uploadInner: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
    },
    uploadCopy: {
      flex: 1,
      minWidth: 0,
      gap: 4,
    },
    uploadText: {
      ...appTheme.typography.body,
      fontWeight: '600',
      color: c.text,
    },
    supportedFormats: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
    },
    previewContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
    },
    previewImage: {
      width: 56,
      height: 56,
      borderRadius: 12,
      backgroundColor: c.borderLight,
    },
    deleteButton: {
      width: 36,
      height: 36,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${c.error}14`,
    },
    uploadIconWrap: {
      width: 40,
      height: 40,
      borderRadius: 20,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.primaryUltra ?? `${c.primary}18`,
    },
    submitButton: {
      marginTop: appTheme.spacing.xs,
      backgroundColor: c.primary,
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: DASH_STUDIO_RADIUS,
      alignItems: 'center',
      minHeight: 52,
      justifyContent: 'center',
    },
    submitButtonDisabled: {
      opacity: 0.55,
    },
    submitButtonText: {
      ...appTheme.typography.body,
      fontWeight: '700',
      color: c.onPrimary,
    },
  });
}

interface PostModalProps {
  visible: boolean;
  customerId: number;
  onClose: () => void;
  onSubmit: (data: CreatePostPayload) => void;
  isLoading?: boolean;
}

export const PostModal: React.FC<PostModalProps> = ({ visible, customerId, onClose, onSubmit, isLoading = false }) => {
  const { t } = useTranslation();
  const rtl = useRtl();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createPostModalStyles(colors), [colors]);
  const [content, setContent] = useState('');
  const [attachment, setAttachment] = useState<string | null>(null);
  const [attachment_type, set_attachment_type] = useState<string | undefined>();
  const [attachment_name, set_attachment_name] = useState<string | undefined>();
  const [attachment_preview, set_attachment_preview] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setContent('');
      setAttachment(null);
      set_attachment_type(undefined);
      set_attachment_name(undefined);
      set_attachment_preview(null);
    }
  }, [visible]);

  const handleClose = () => {
    onClose();
  };

  const remaining = COMMUNITY_POST_MAX_CHARS - content.length;
  const can_post = content.trim().length > 0 && !isLoading;

  const handleSubmit = () => {
    const trimmed = content.trim();
    if (!trimmed) {
      Alert.alert(t('common.error'), t('community.enterContentError'));
      return;
    }
    onSubmit({
      customer_id: customerId,
      content: trimmed,
      attachment: attachment ?? undefined,
      attachment_type,
      attachment_name,
    });
  };

  const apply_asset = (asset: { uri?: string; type?: string; fileName?: string } | undefined) => {
    if (!asset?.uri) {
      return;
    }
    setAttachment(asset.uri);
    set_attachment_preview(asset.uri);
    set_attachment_type(asset.type || 'image/jpeg');
    set_attachment_name(asset.fileName || 'community-post.jpg');
  };

  const openGallery = () => {
    if (!launchImageLibrary) {
      Alert.alert(t('common.error'), t('community.failedToSelectImage'));
      return;
    }
    launchImageLibrary({ mediaType: 'photo', selectionLimit: 1 }, (res: any) => {
      if (res?.didCancel) {
        return;
      }
      if (res?.errorCode) {
        Alert.alert(t('common.error'), t('community.cameraPermissionDenied'));
        return;
      }
      apply_asset(res?.assets?.[0]);
    });
  };

  const openCamera = () => {
    if (!launchCamera) {
      Alert.alert(t('common.error'), t('community.failedToSelectImage'));
      return;
    }
    launchCamera({ mediaType: 'photo', saveToPhotos: false }, (res: any) => {
      if (res?.didCancel) {
        return;
      }
      if (res?.errorCode) {
        Alert.alert(t('common.error'), t('community.cameraPermissionDenied'));
        return;
      }
      apply_asset(res?.assets?.[0]);
    });
  };

  const openCameraOrGallery = () => {
    showActionSheet({
      title: t('community.uploadImage'),
      message: t('community.chooseOption'),
      cancelLabel: t('common.cancel'),
      options: [
        { label: t('community.chooseFromGallery'), onSelect: openGallery },
        { label: t('community.captureImage'), onSelect: openCamera },
      ],
    });
  };

  const deleteImage = () => {
    setAttachment(null);
    set_attachment_preview(null);
    set_attachment_type(undefined);
    set_attachment_name(undefined);
  };

  const writing = rtl.isRTL ? 'rtl' : 'ltr';
  const text_align = rtl.isRTL ? 'right' : 'left';

  return (
    <BottomSheetPopup
      visible={visible}
      onClose={handleClose}
      onRequestClose={handleClose}
      title={t('community.newPost')}
      showDragHandle
      animationType="slide"
      keyboardAvoiding
      sheetStyle={{ maxHeight: '90%' }}>
      <View style={styles.content}>
        <Input
          placeholder={t('community.enterPostPlaceholder')}
          value={content}
          onChangeText={text => setContent(text.slice(0, COMMUNITY_POST_MAX_CHARS))}
          multiline
          numberOfLines={5}
          textAlignVertical="top"
          maxLength={COMMUNITY_POST_MAX_CHARS}
          containerStyle={{ marginBottom: 0 }}
          inputStyle={{
            minHeight: 120,
            textAlign: text_align,
            writingDirection: writing,
            ...(Platform.OS === 'android' ? { includeFontPadding: false } : null),
          }}
        />
        <View style={styles.composerMeta}>
          <Text style={styles.charsText}>{t('community.charsRemaining', { count: formatNumber(remaining) })}</Text>
        </View>

        {attachment_preview ? (
          <View style={[styles.uploadCard]}>
            <View style={[styles.uploadInner, horizontalLtrDirectionStyle]}>
              <View style={styles.uploadCopy}>
                <Text style={[styles.uploadText, { textAlign: text_align, writingDirection: writing }]}>{t('community.addPhoto')}</Text>
                <Text style={[styles.supportedFormats, { textAlign: text_align, writingDirection: writing }]}>
                  {t('community.supportedImageFormats')}
                </Text>
              </View>
              <View style={[styles.previewContainer, horizontalLtrDirectionStyle]}>
                <Image source={{ uri: attachment_preview }} style={styles.previewImage} />
                <Clickable
                  style={styles.deleteButton}
                  onPress={deleteImage}
                  haptics
                  accessibilityLabel={t('common.delete')}
                  accessibilityRole="button">
                  <SvgIcon name="trash" size={18} color={colors.error} />
                </Clickable>
              </View>
            </View>
            <Clickable
              onPress={openCameraOrGallery}
              pressScale={PRESS_PRESETS.listRow.scale}
              style={{ marginTop: appTheme.spacing.sm }}
              accessibilityLabel={t('community.uploadImage')}
              accessibilityRole="button">
              <Text style={[styles.supportedFormats, { color: colors.primary, fontWeight: '600', textAlign: text_align }]}>
                {t('community.chooseFromGallery')}
              </Text>
            </Clickable>
          </View>
        ) : (
          <Clickable
            style={styles.uploadCard}
            onPress={openCameraOrGallery}
            pressScale={PRESS_PRESETS.listRow.scale}
            accessibilityLabel={t('community.uploadImage')}
            accessibilityRole="button">
            <View style={[styles.uploadInner, horizontalLtrDirectionStyle]}>
              <View style={styles.uploadCopy}>
                <Text style={[styles.uploadText, { textAlign: text_align, writingDirection: writing }]}>{t('community.addPhoto')}</Text>
                <Text style={[styles.supportedFormats, { textAlign: text_align, writingDirection: writing }]}>
                  {t('community.supportedImageFormats')}
                </Text>
              </View>
              <View style={styles.uploadIconWrap}>
                <SvgIcon name="plus" size={22} color={colors.primary} />
              </View>
            </View>
          </Clickable>
        )}

        <Clickable
          onPress={handleSubmit}
          disabled={!can_post}
          haptics={can_post}
          pressScale={PRESS_PRESETS.button.scale}
          style={[styles.submitButton, !can_post ? styles.submitButtonDisabled : null]}
          accessibilityLabel={t('community.post')}
          accessibilityRole="button">
          <View>
            {isLoading ? (
              <ButtonLoader size={18} color={colors.onPrimary} />
            ) : (
              <Text style={styles.submitButtonText}>{t('community.post')}</Text>
            )}
          </View>
        </Clickable>
      </View>
    </BottomSheetPopup>
  );
};
